import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { setGlobalDateFormat } from "common/service/CommonService";

interface DateFormatContextType {
  /** Currently active date format (e.g. "DD/MM/YYYY" or "dd/mm/yyyy") */
  dateFormat: string;
  /** Update the global date format and store in localStorage */
  setDateFormat: (format: string) => void;
  isLoading: boolean;
}

const DEFAULT_DATE_FORMAT = "dd/mm/yyyy";
const STORAGE_KEY = "dateFormat";

const DateFormatContext = createContext<DateFormatContextType | undefined>(undefined);

export const DateFormatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dateFormat, setDateFormatState] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const savedFormat = sessionStorage.getItem(STORAGE_KEY);
      if (savedFormat) return savedFormat;
    }
    return DEFAULT_DATE_FORMAT;
  });

  // Keep CommonService helper synchronized with active date format
  useEffect(() => {
    setGlobalDateFormat(dateFormat);
  }, [dateFormat]);

  const setDateFormat = useCallback((format: string) => {
    if (!format) return;
    setDateFormatState(format);
    // setGlobalDateFormat(format);
    if (typeof window !== "undefined") {
      sessionStorage.setItem(STORAGE_KEY, format);
    }
  }, []);

  return (
    <DateFormatContext.Provider
      value={{
        dateFormat,
        setDateFormat,
        isLoading: false,
      }}
    >
      {children}
    </DateFormatContext.Provider>
  );
};

export const useDateFormat = (): DateFormatContextType => {
  const context = useContext(DateFormatContext);
  if (context === undefined) {
    throw new Error("useDateFormat must be used within a <DateFormatProvider>");
  }
  return context;
};

